import { apiClient, ApiError } from '../utils/apiClient.js';
import { useAuthStore } from '../stores/authStore.js';
import { db } from '../db/index.js';
import { accountId } from '../db/scope.js';
import { waitForGuestClaim } from '../db/claim.js';
import { rekeyLocalTab } from '../db/rekey.js';
import {
  getPendingUploads,
  getAllPendingUploads,
  getTabTombstonesToPush,
  hardDeleteLocalTabRow,
  clearPendingServerDelete,
  markTabUploaded,
  recordUploadFailure,
  markTabReferencesDirty
} from '../db/operations.js';
import { inferGpExtension } from '../utils/gpValidation.js';
import { newId } from '../utils/id.js';
import { nextUploadDelayMs, UPLOAD_MAX_DELAY_MS } from '../utils/uploadBackoff.js';
import { LIBRARY_CHANGED_EVENT, AUTH_LOGOUT_EVENT } from './events.js';
import { requestRoutineSync } from './routineSync.js';
import { requestFavoriteSync } from './favoriteSync.js';
import { requestPlaylistSync } from './playlistSync.js';
import { requestLyricSync } from './lyricSync.js';
import { queueTabEdit } from '../db/tabEdits.js';
import { requestTabEditSync } from './tabEditSync.js';
import type { TabSong } from '../types/index.js';

export { LIBRARY_CHANGED_EVENT };

const MAX_DELAY_MS = UPLOAD_MAX_DELAY_MS;
export { nextUploadDelayMs };

const safeName = (title: string): string => {
  const cleaned = title.replace(/[^A-Za-z0-9._-]/g, '');
  return cleaned || 'tab';
};

const statusOf = (err: unknown): number | undefined => (err instanceof ApiError ? err.status : undefined);

let inFlight: Promise<void> | null = null;
let rerunRequested = false;
let timer: ReturnType<typeof setTimeout> | null = null;

/** Clears the scheduled retry (logout). */
export function cancelScheduledUploads(): void {
  if (timer) clearTimeout(timer);
  timer = null;
}

if (typeof window !== 'undefined') {
  window.addEventListener(AUTH_LOGOUT_EVENT, cancelScheduledUploads);
}

/** After a reconnect, rows that failed with a network error are retried at once. */
export async function resetNetworkBackoff(): Promise<void> {
  const uid = accountId();
  if (!uid) return;
  try {
    const rows = await getAllPendingUploads(uid);
    for (const r of rows) {
      if (r.uploadError === 'network' && r.nextUploadAt) {
        const next: TabSong = { ...r };
        delete next.nextUploadAt;
        await db.tabs.put(next);
      }
    }
  } catch (err) {
    console.warn('tabUploadSync: resetNetworkBackoff failed', err);
  }
}

interface UploadResponse {
  id?: string;
  fileUrl?: string;
  status?: TabSong['status'];
  uploaderId?: string;
}

async function pushServerDeletes(uid: string): Promise<boolean> {
  let changed = false;
  const rows = await getTabTombstonesToPush(uid);
  for (const row of rows) {
    try {
      await apiClient.delete(`/api/tabs/${row.id}`);
      await hardDeleteLocalTabRow(row.id);
      changed = true;
    } catch (err) {
      const status = statusOf(err);
      if (status === 404) {
        await hardDeleteLocalTabRow(row.id);
        changed = true;
      } else if (status === 409) {
        // Moderated in the meantime (PENDING/APPROVED): keep the local hide, stop asking the server.
        await clearPendingServerDelete(row.id);
      } else if (status === 401) {
        throw err;
      } else if (status !== undefined && status >= 400 && status < 500) {
        await clearPendingServerDelete(row.id);
      } else {
        throw err; // network / 5xx: stop the run
      }
    }
  }
  return changed;
}

/** Returns true when anything changed. Throws only to stop the run (callers swallow). */
async function runOnce(): Promise<void> {
  const { token, isGuest, user } = useAuthStore.getState();
  if (!token || isGuest || !user) return;
  const uid = user.id;
  const capturedToken = token;

  await waitForGuestClaim();

  let changed = false;
  try {
    // Deletes first
    try {
      if (await pushServerDeletes(uid)) changed = true;
    } catch (err) {
      console.warn('tabUploadSync: server delete step stopped', err);
      return;
    }

    const rows = await getPendingUploads(uid, new Date().toISOString());
    for (const row of rows) {
      if (!row.localBlob) {
        console.info('tabUploadSync: skipping tab without a local file', row.id);
        continue;
      }

      const form = new FormData();
      form.append('id', row.id); // before `file`, so the server can read it while streaming
      form.append('title', row.title);
      form.append('artist', row.artist);
      form.append('tuning', row.tuning);
      form.append('publish', 'false');
      // FRE-188: before `file`; empty string = unrated
      form.append('rhythm_difficulty', row.rhythmDifficulty ? String(row.rhythmDifficulty) : '');
      form.append('lead_difficulty', row.leadDifficulty ? String(row.leadDifficulty) : '');
      form.append('lyrics', row.lyricsContent ?? '');
      if (row.tracks && row.tracks.length > 0) {
        form.append(
          'tracks',
          JSON.stringify(
            row.tracks.map((t) => ({ trackIndex: t.trackIndex, name: t.name }))
          )
        );
      }
      const fileName = row.localFileName ?? safeName(row.title) + (inferGpExtension(row.localBlob) ?? '.gp5');
      form.append('file', new File([row.localBlob], fileName));

      // Account guard: no await between this check and the request being issued.
      if (useAuthStore.getState().token !== capturedToken || accountId() !== row.ownerId) return;

      let res: UploadResponse | undefined;
      try {
        res = await apiClient.post<UploadResponse>('/api/tabs/upload', form);
      } catch (err) {
        const status = statusOf(err);
        const attemptsNow = (row.uploadAttempts ?? 0) + 1;
        if (status === 401) return;
        if (status === 409 && err instanceof ApiError && err.data.code === 'TAB_ID_TAKEN') {
          await rekeyLocalTab(row.id, newId());
          changed = true;
          rerunRequested = true; // upload it under the new id right away
          continue;
        }
        if (status === 413) {
          await recordUploadFailure(row.id, { uploadBlocked: 1, uploadError: 'too_large' });
          changed = true;
          continue;
        }
        if (status === 400 || status === 403 || status === 415 || status === 422) {
          await recordUploadFailure(row.id, { uploadBlocked: 1, uploadError: 'rejected' });
          changed = true;
          continue;
        }
        if (status === undefined || status >= 500 || status === 408 || status === 429) {
          let delay = nextUploadDelayMs(attemptsNow);
          if (status === 429 && err instanceof ApiError && typeof err.data.retryAfter === 'number') {
            delay = Math.max(delay, err.data.retryAfter * 1000);
          }
          await recordUploadFailure(row.id, {
            uploadAttempts: attemptsNow,
            nextUploadAt: new Date(Date.now() + delay).toISOString(),
            uploadError: status === undefined ? 'network' : 'server'
          });
          changed = true;
          return; // server or network is down: the rest waits
        }
        // Any other 4xx is a permanent rejection of this file.
        await recordUploadFailure(row.id, { uploadBlocked: 1, uploadError: 'rejected' });
        changed = true;
        continue;
      }

      if (!res || typeof res !== 'object' || typeof res.id !== 'string') {
        // e.g. a catch-all mock answering `[]`: treat as a server failure
        const attemptsNow = (row.uploadAttempts ?? 0) + 1;
        await recordUploadFailure(row.id, {
          uploadAttempts: attemptsNow,
          nextUploadAt: new Date(Date.now() + nextUploadDelayMs(attemptsNow)).toISOString(),
          uploadError: 'server'
        });
        changed = true;
        return;
      }

      if (res.id !== row.id) await rekeyLocalTab(row.id, res.id); // API ignored our id
      const stillThere = await markTabUploaded(res.id, {
        fileUrl: res.fileUrl,
        status: res.status ?? 'PRIVATE',
        uploaderId: res.uploaderId
      });
      changed = true;
      if (stillThere) {
        const after = await db.tabs.get(res.id);
        if (after) {
          const detailsChanged =
            after.title !== row.title ||
            after.artist !== row.artist ||
            after.tuning !== row.tuning;
          const lyricsChanged =
            (after.lyricsContent ?? '') !== (row.lyricsContent ?? '');
          const difficultyChanged =
            (after.rhythmDifficulty ?? null) !== (row.rhythmDifficulty ?? null) ||
            (after.leadDifficulty ?? null) !== (row.leadDifficulty ?? null);

          let queuedAny = false;
          if (detailsChanged) {
            await queueTabEdit({
              id: newId(),
              tabId: res.id,
              ownerId: uid,
              kind: 'DETAILS',
              payload: {
                title: after.title,
                artist: after.artist,
                tuning: after.tuning
              },
              baseRevision: after.revision ?? 1,
              status: 'QUEUED',
              mode: 'direct'
            });
            queuedAny = true;
          }

          if (difficultyChanged) {
            await queueTabEdit({
              id: newId(),
              tabId: res.id,
              ownerId: uid,
              kind: 'DIFFICULTY',
              payload: {
                rhythm_difficulty: after.rhythmDifficulty ?? null,
                lead_difficulty: after.leadDifficulty ?? null
              },
              baseRevision: after.revision ?? 1,
              status: 'QUEUED',
              mode: 'direct'
            });
            queuedAny = true;
          }

          if (lyricsChanged) {
            await queueTabEdit({
              id: newId(),
              tabId: res.id,
              ownerId: uid,
              kind: 'LYRICS',
              payload: {
                content: after.lyricsContent ?? ''
              },
              baseRevision: after.revision ?? 1,
              status: 'QUEUED',
              mode: 'direct'
            });
            queuedAny = true;
          }

          if (queuedAny) {
            requestTabEditSync();
          }
        }
        await markTabReferencesDirty(res.id, uid);
      } else {
        // The user deleted the tab while it was uploading: remove the server copy too.
        try {
          await apiClient.delete(`/api/tabs/${res.id}`);
        } catch (err) {
          console.warn('tabUploadSync: could not delete the orphaned server copy', err);
        }
      }
    }
  } finally {
    if (changed) {
      window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
      requestRoutineSync();
      requestFavoriteSync();
      requestPlaylistSync();
      requestLyricSync();
    }
    await scheduleNext(uid);
  }
}

async function scheduleNext(uid: string): Promise<void> {
  cancelScheduledUploads();
  try {
    const rows = await getAllPendingUploads(uid);
    const times = rows
      .filter((r) => r.uploadBlocked !== 1 && r.nextUploadAt)
      .map((r) => Date.parse(r.nextUploadAt as string))
      .filter((t) => !Number.isNaN(t));
    if (times.length === 0) return;
    const wait = Math.min(Math.max(Math.min(...times) - Date.now(), 1000), MAX_DELAY_MS);
    timer = setTimeout(requestTabUploadSync, wait);
  } catch {
    // Dexie not ready: no timer
  }
}

/** Upload queued private tabs (and send pending server deletes). Single in-flight run plus one rerun. */
export function syncTabUploads(): Promise<void> {
  if (inFlight) {
    rerunRequested = true;
    return inFlight;
  }
  inFlight = (async () => {
    try {
      do {
        rerunRequested = false;
        await runOnce();
      } while (rerunRequested);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Fire and forget; no-op offline or as a guest; errors are swallowed. */
export function requestTabUploadSync(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  if (!accountId()) return;
  syncTabUploads().catch((err) => {
    console.warn('tabUploadSync: background sync failed', err);
  });
}
